// src/utils/smartStyleLoader.js
// Продакшн версия - без отладочных логов

class SmartStyleLoader {
  constructor(options = {}) {
    this.options = options;
  }

  async loadAllStyles() {
    let totalLoaded = 0;

    // Загружаем базовые стили первыми
    totalLoaded += await this.loadPriorityStyles();

    // Загружаем все CSS из компонентов и страниц
    totalLoaded += await this.loadAllProjectStyles();

    // Загружаем финальные стили
    totalLoaded += await this.loadFinalStyles();

    return totalLoaded;
  }

  async loadPriorityStyles() {
    const priorityFiles = [
      () => import('../styles/variables.css'),
      () => import('../styles/global.css'),
    ];

    let loaded = 0;
    for (const importFn of priorityFiles) {
      if (await this.loadFileAsync(importFn)) loaded++;
    }
    
    return loaded;
  }

  async loadAllProjectStyles() {
    let loaded = 0;

    loaded += await this.loadLayoutStyles();
    loaded += await this.loadCommonStyles();
    loaded += await this.loadPageStyles();
    loaded += await this.loadPagesSubfolderStyles();
    loaded += await this.loadChannelAnalysisStyles();
    loaded += await this.loadChannelDiscoveryStyles();
    loaded += await this.loadResearchStyles();
    loaded += await this.loadNicheInsightsStyles();
    loaded += await this.loadVideoAnalysisStyles();
    loaded += await this.loadVideoComparisonStyles();
    loaded += await this.loadVideoDiscoveryStyles();

    return loaded;
  }

  async loadLayoutStyles() {
    const layoutFiles = [
      () => import('../components/layout/Layout.css'),
      () => import('../components/layout/Sidebar.css'), 
      () => import('../components/layout/Header.css'),
      () => import('../components/layout/Footer.css'),
      () => import('../components/layout/sidebar-base.css'),
      () => import('../components/layout/sidebar-filters.css'),
      () => import('../components/layout/sidebar-mobile.css'),
      () => import('../components/layout/sidebar-navigation.css'),
      () => import('../components/layout/sidebar-themes.css'),
    ];

    return await this.loadStylesGroup(layoutFiles);
  }

  async loadPageStyles() {
    const pageFiles = [
      () => import('../pages/AboutPage.css'),
      () => import('../pages/ChannelAnalysisPage.css'),
      () => import('../pages/ChannelComparisonPage.css'),
      () => import('../pages/ChannelDiscoveryPage.css'),
      () => import('../pages/DashboardPage.css'),
      () => import('../pages/HomePage.css'),
      () => import('../pages/KeywordsPage.css'),
      () => import('../pages/MarketPlacePage.css'),
      () => import('../pages/MonetizationPage.css'),
      () => import('../pages/MostViewedPage.css'),
      () => import('../pages/NicheInsightsPage.css'),
      () => import('../pages/NotFoundPage.css'),
      () => import('../pages/VideoAnalysisPage.css'),
      () => import('../pages/VideoComparisonPage.css'),
      () => import('../pages/VideoDiscoveryPage.css'),
      () => import('../pages/VideoInfoPage.css'),
      () => import('../pages/YTDashboardPage.css'),
    ];

    return await this.loadStylesGroup(pageFiles);
  }

  async loadPagesSubfolderStyles() {
    const subfolderFiles = [
      () => import('../pages/subscription/PricingPage.css'),
    ];

    return await this.loadStylesGroup(subfolderFiles);
  }

  async loadCommonStyles() {
    const commonFiles = [
      () => import('../components/common/Button.css'),
      () => import('../components/common/Card.css'),
      () => import('../components/common/ErrorMessage.css'),
      () => import('../components/common/Input.css'),
      () => import('../components/common/Loader.css'),
      () => import('../components/common/Modal.css'),
      () => import('../components/common/Table.css'),
      () => import('../components/common/Tabs.css'),
    ];

    return await this.loadStylesGroup(commonFiles);
  }

  async loadChannelAnalysisStyles() {
    const channelAnalysisFiles = [
      () => import('../components/ChannelAnalysis/ChannelAIAnalysis.css'),
      () => import('../components/ChannelAnalysis/ChannelAnalysisContent.css'),
      () => import('../components/ChannelAnalysis/ChannelCommon.css'),
      () => import('../components/ChannelAnalysis/ChannelForm.css'),
      () => import('../components/ChannelAnalysis/ChannelMonetization.css'),
    ];

    return await this.loadStylesGroup(channelAnalysisFiles);
  }

  async loadChannelDiscoveryStyles() {
    const channelDiscoveryFiles = [
      () => import('../components/ChannelDiscovery/CategorySelector.css'),
      () => import('../components/ChannelDiscovery/ChannelCard.css'),
      () => import('../components/ChannelDiscovery/ChannelGrid.css'),
      () => import('../components/ChannelDiscovery/ChannelList.css'),
      () => import('../components/ChannelDiscovery/ChannelRegionSelector.css'),
      () => import('../components/ChannelDiscovery/ChannelUrlSearch.css'),
      () => import('../components/ChannelDiscovery/DiscoveryFilters.css'),
      () => import('../components/ChannelDiscovery/RankingMethodSelector.css'),
      () => import('../components/ChannelDiscovery/TagsFilter.css'),
    ];

    return await this.loadStylesGroup(channelDiscoveryFiles);
  }

  async loadResearchStyles() {
    const researchFiles = [
      // Research/Dashboard
      () => import('../components/Research/Dashboard/DashboardFilters.css'),
      () => import('../components/Research/Dashboard/HotTagsBlock.css'),
      () => import('../components/Research/Dashboard/TopVideosBlock.css'),
      
      // Research/Keywords
      () => import('../components/Research/Keywords/KeywordFilters.css'),
      () => import('../components/Research/Keywords/KeywordsDashboard.css'),
      () => import('../components/Research/Keywords/KeywordsDetailed.css'),
      () => import('../components/Research/Keywords/KeywordsHeader.css'),
      
      // Research/Monetization
      () => import('../components/Research/Monetization/MonetizationFilters.css'),
      () => import('../components/Research/Monetization/MonetizationStats.css'),
      () => import('../components/Research/Monetization/MonetizationTable.css'),
      
      // Research/MostViewed
      () => import('../components/Research/MostViewed/MostViewedFilters.css'),
      () => import('../components/Research/MostViewed/MostViewedHeader.css'),
      () => import('../components/Research/MostViewed/MostViewedList.css'),
      () => import('../components/Research/MostViewed/VideoListItem.css'),
      () => import('../components/Research/MostViewed/VideoSearchBar.css'),
    ];

    return await this.loadStylesGroup(researchFiles);
  }

  async loadNicheInsightsStyles() {
    const nicheFiles = [
      () => import('../components/NicheInsights/NicheMetricsCards.css'),
      () => import('../components/NicheInsights/NicheOverview.css'),
    ];

    return await this.loadStylesGroup(nicheFiles);
  }

  async loadVideoAnalysisStyles() {
    const videoAnalysisFiles = [
      () => import('../components/VideoAnalysis/CommentAnalysis.css'),
      () => import('../components/VideoAnalysis/ContentAnalysis.css'),
      () => import('../components/VideoAnalysis/ContentStructure.css'),
      () => import('../components/VideoAnalysis/EngagementMetrics.css'),
      () => import('../components/VideoAnalysis/RetentionChart.css'),
      () => import('../components/VideoAnalysis/TechnicalAnalysis.css'),
      () => import('../components/VideoAnalysis/VideoAnalysisContent.css'),
      () => import('../components/VideoAnalysis/VideoDetails.css'),
      () => import('../components/VideoAnalysis/VideoForm.css'),
    ];

    return await this.loadStylesGroup(videoAnalysisFiles);
  }

  async loadVideoComparisonStyles() {
    const videoComparisonFiles = [
      () => import('../components/VideoComparison/VideoComparisonChart.css'),
      () => import('../components/VideoComparison/VideoComparisonTable.css'),
      () => import('../components/VideoComparison/VideoMetricsSelector.css'),
    ];

    return await this.loadStylesGroup(videoComparisonFiles);
  }

  async loadVideoDiscoveryStyles() {
    const videoDiscoveryFiles = [
      () => import('../components/VideoDiscovery/ApiModeToggle.css'),
      () => import('../components/VideoDiscovery/LanguageSelector.css'),
      () => import('../components/VideoDiscovery/RegionModeSelector.css'),
      () => import('../components/VideoDiscovery/TimeWindowSelector.css'),
      () => import('../components/VideoDiscovery/VideoCard.css'),
      () => import('../components/VideoDiscovery/VideoCategorySelector.css'),
      () => import('../components/VideoDiscovery/VideoDiscoveryFilters.css'),
      () => import('../components/VideoDiscovery/VideoGrid.css'),
      () => import('../components/VideoDiscovery/VideoList.css'),
      () => import('../components/VideoDiscovery/VideoRankingMethodSelector.css'),
      () => import('../components/VideoDiscovery/VideoRegionSelector.css'),
      () => import('../components/VideoDiscovery/VideoTagsFilter.css'),
      () => import('../components/VideoDiscovery/VideoUrlSearch.css'),
    ];

    return await this.loadStylesGroup(videoDiscoveryFiles);
  }

  async loadFinalStyles() {
    const finalFiles = [
      () => import('../styles/force-dark-theme.css'),
    ];

    return await this.loadStylesGroup(finalFiles);
  }

  // Вспомогательный метод для загрузки группы стилей
  async loadStylesGroup(importFunctions) {
    let loaded = 0;
    
    // Загружаем все стили параллельно для лучшей производительности
    const results = await Promise.allSettled(
      importFunctions.map(importFn => this.loadFileAsync(importFn))
    );
    
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value) {
        loaded++;
      }
    });
    
    return loaded;
  }

  async loadFileAsync(importFunction) {
    try {
      await importFunction();
      return true;
    } catch (error) {
      // Полностью тихая обработка ошибок
      return false;
    }
  }
}

// Экспортируем готовую функцию
const loadAllStyles = async (customOptions = {}) => {
  const loader = new SmartStyleLoader(customOptions);
  return await loader.loadAllStyles();
};

export default loadAllStyles;

// Экспортируем также класс для более тонкой настройки
export { SmartStyleLoader };